import path from "path";
import mime from "mime-types";
import { createFilter } from "@rollup/pluginutils";

const { posix, sep } = path;
const defaultInclude = [
  "**/*.svg",
  "**/*.png",
  "**/*.jp(e)?g",
  "**/*.gif",
  "**/*.webp",
];

export default function url(options = {}) {
  const {
    limit = 14 * 1024,
    include = defaultInclude,
    exclude,
    publicPath = "",
    emitFiles = true,
    fileName = "[hash][extname]",
    updateExport = (data) => data,
    // @typedef {import('../fs-iface').IsomorphicRollupFs} IsomorphicRollupFs
  } = options;
  const filter = createFilter(include, exclude);

  const copies = Object.create(null);

  return {
    name: "url",
    async load(id) {
      if (!filter(id)) {
        return null;
      }
      this.addWatchFile(id);
      const stats = await this.fs.stat(id);
      const content = await this.fs.readFile(id);

      let data;
      if ((limit && stats.size > limit) || limit === 0) {
        const hash = Buffer.from(await crypto.subtle.digest("SHA-1", content))
          .toString("hex")
          .substring(0, 16);
        const ext = path.extname(id);
        const name = path.basename(id, ext);
        // Determine the directory name of the file based
        // on either the relative path provided in options,
        // or the parent directory
        const relativeDir = options.sourceDir
          ? path.relative(options.sourceDir, path.dirname(id))
          : path.dirname(id).split(sep).pop();

        // Generate the output file name based on some string
        // replacement parameters
        const outputFileName = fileName
          .replace(/\[hash\]/g, hash)
          .replace(/\[extname\]/g, ext)
          // use `sep` for windows environments
          .replace(
            /\[dirname\]/g,
            relativeDir === "" ? "" : `${relativeDir}${sep}`,
          )
          .replace(/\[name\]/g, name);
        // Windows fix - exports must be in unix format
        data = `${publicPath}${outputFileName.split(sep).join(posix.sep)}`;
        copies[id] = outputFileName;
      } else {
        const mimetype = mime.lookup(id);
        const isSVG = mimetype === "image/svg+xml";
        const buffer = Buffer.from(content);
        data = isSVG ? encodeSVG(buffer) : buffer.toString("base64");
        const encoding = isSVG ? "" : ";base64";
        data = `data:${mimetype}${encoding},${data}`;
      }
      data = updateExport(data);
      return `export default "${data}"`;
    },
    generateBundle: async function write(outputOptions) {
      // Allow skipping saving files for server side builds.
      if (!emitFiles) return;

      for (const name of Object.keys(copies)) {
        const fileName = copies[name];
        const content = await this.fs.readFile(name);
        this.emitFile({
          type: "asset",
          fileName,
          source: content,
        });
      }
    },
  };
}

// https://github.com/filamentgroup/directory-encoder/blob/master/lib/svg-uri-encoder.js
function encodeSVG(buffer) {
  return (
    encodeURIComponent(
      buffer
        .toString("utf-8")
        // strip newlines and tabs
        .replace(/[\n\r]/gim, "")
        .replace(/\t/gim, " ")
        // strip comments
        .replace(/<!--(.*(?=-->))-->/gim, "")
        // replace
        .replace(/'/gim, "\\i"),
    )
      // encode brackets
      .replace(/\(/g, "%28")
      .replace(/\)/g, "%29")
  );
}
